import { rem } from '@triumph/design-system';
import { Tag } from 'primereact/tag';
import { ReactNode, useId, useState } from 'react';

import { TabDataState } from '@/components/shared/components/TabDataState';
import { getTabBadgeStyle } from '@/components/shared/constants/conditionsBadge';

import { DDDCard, DDDCardTitle } from '../../Applicant.StyledComponents';
import {
    CardChevron,
    CardStateBody,
    CardToggle,
    CollapsibleCardHeader,
} from './ApplicantRelatedAccounts.StyledComponents';

interface RelatedAccountsCardProps extends Omit<React.ComponentPropsWithRef<'div'>, 'title' | 'children'> {
    'data-testid': string;
    title: string;
    count: number;
    isLoading: boolean;
    isLoadingError: boolean;
    onRetry?: () => void;
    emptyMessage: string;
    errorMessage: string;
    children: ReactNode;
}

// One collapsible DDD-style card per related-accounts source: title + count badge header, then the accounts.
export const RelatedAccountsCard = ({
    title,
    count,
    isLoading,
    isLoadingError,
    onRetry,
    emptyMessage,
    errorMessage,
    children,
    'data-testid': testId,
    ...props
}: RelatedAccountsCardProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const bodyId = useId();
    const isCountKnown = !isLoading && !isLoadingError;
    const isEmpty = count === 0;
    const isBodyHidden = !isOpen && isCountKnown;
    const state = (
        <TabDataState
            alignStart
            isLoading={isLoading}
            isLoadingError={isLoadingError}
            onRetry={onRetry}
            isEmpty={isEmpty}
            emptyMessage={emptyMessage}
            errorMessage={errorMessage}
        >
            {children}
        </TabDataState>
    );

    return (
        <DDDCard {...props} data-testid={testId}>
            <CollapsibleCardHeader $collapsed={isBodyHidden}>
                <DDDCardTitle>
                    <CardToggle
                        type="button"
                        onClick={() => setIsOpen((open) => !open)}
                        aria-expanded={isOpen}
                        aria-controls={bodyId}
                        data-testid={`${testId}-toggle`}
                    >
                        <CardChevron aria-hidden>{isOpen ? '▾' : '▸'}</CardChevron>
                        {title}
                        {isCountKnown ? (
                            <Tag
                                severity="info"
                                value={count}
                                rounded
                                style={{
                                    fontSize: rem(14),
                                    padding: `${rem(1)} ${rem(6)}`,
                                    ...getTabBadgeStyle(count),
                                }}
                            />
                        ) : null}
                    </CardToggle>
                </DDDCardTitle>
            </CollapsibleCardHeader>
            {/* Hidden rather than unmounted so the Originations card's infinite-scroll observer keeps its target.
                Loading and error stay visible while collapsed so a failed fetch isn't hidden behind the toggle. */}
            <div id={bodyId} hidden={isBodyHidden}>
                {/* Rows carry their own padding; the loading/empty/error message needs the card's. */}
                {isLoading || isLoadingError || isEmpty ? <CardStateBody>{state}</CardStateBody> : state}
            </div>
        </DDDCard>
    );
};
